import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://dbasesolutions.in";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const DEFAULT_TITLE = "D Base Solutions | Enterprise Software, EdTech & University ERP";
const DEFAULT_DESCRIPTION =
  "Official website of D Base Solutions Pvt Ltd (DBS) - Premier enterprise software and EdTech company delivering EdVedha LMS, UNIcode University Exam Suite, iCampus BOAT ERP, and exam automation across India.";
const DEFAULT_KEYWORDS =
  "D Base Solutions, D Base Solutions Pvt Ltd, DBS, EdVedha, UNIcode, iCampus BEAT, iCampus BOAT, ERP software, university exam management, education software India, Eluru, Vijayawada";

function normalizePath(pathOrUrl) {
  if (!pathOrUrl) return "/";
  let pathname = pathOrUrl;
  try {
    if (pathname.startsWith("http://") || pathname.startsWith("https://")) {
      const parsed = new URL(pathname);
      pathname = parsed.pathname;
    }
  } catch (e) {
    // fallback
  }
  if (!pathname.startsWith("/")) pathname = "/" + pathname;
  if (pathname.length > 1 && pathname.endsWith("/")) {
    pathname = pathname.slice(0, -1);
  }
  return pathname.toLowerCase();
}

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  path,
  ogImage,
  ogType = "website",
  noIndex = false,
  breadcrumbs = []
}) => {
  const location = useLocation();

  useEffect(() => {
    const currentPath = normalizePath(location.pathname);
    const targetPath = (path || canonical) ? normalizePath(path || canonical) : currentPath;

    // CRITICAL SEO GUARD: If this component specifies a route different from the current page
    // (e.g. <Contact /> or <About /> embedded as a section on the Home page "/"),
    // do NOT let it overwrite the active page's metadata or canonical tag.
    if (targetPath !== currentPath) {
      return;
    }

    const fullTitle = title
      ? title.includes("D Base Solutions") || title.includes("DBS")
        ? title
        : `${title} | D Base Solutions Pvt Ltd`
      : DEFAULT_TITLE;

    const metaDescription = description || DEFAULT_DESCRIPTION;
    const metaKeywords = keywords || DEFAULT_KEYWORDS;
    
    // Ensure homepage canonical is always exactly https://dbasesolutions.in/
    const currentCanonical = canonical
      ? (canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`)
      : `${BASE_URL}${currentPath === "/" ? "/" : currentPath}`;

    const previewImage = ogImage
      ? (ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`)
      : DEFAULT_IMAGE;

    // 1. Update Document Title
    document.title = fullTitle;

    // 2. Helper to set/create <meta> tags
    const setMetaTag = (attributeName, attributeValue, content) => {
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // 3. Set standard meta tags
    setMetaTag("name", "title", fullTitle);
    setMetaTag("name", "description", metaDescription);
    setMetaTag("name", "keywords", metaKeywords);
    setMetaTag("name", "robots", noIndex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    setMetaTag("name", "googlebot", noIndex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    setMetaTag("name", "bingbot", noIndex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // 4. Update Canonical Link
    let canonicalLink = document.getElementById("canonical-link") || document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      canonicalLink.setAttribute("id", "canonical-link");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", currentCanonical);

    // 5. Open Graph Meta Tags
    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:site_name", "D Base Solutions Pvt Ltd");
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", metaDescription);
    setMetaTag("property", "og:url", currentCanonical);
    setMetaTag("property", "og:image", previewImage);
    setMetaTag("property", "og:image:secure_url", previewImage);

    // 6. Twitter Card Meta Tags
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", metaDescription);
    setMetaTag("name", "twitter:image", previewImage);

    // 7. Inject Breadcrumbs Schema if provided
    let breadcrumbsScript = document.getElementById("seo-breadcrumbs-schema");
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url ? (item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`) : currentCanonical
        }))
      };

      if (!breadcrumbsScript) {
        breadcrumbsScript = document.createElement("script");
        breadcrumbsScript.setAttribute("type", "application/ld+json");
        breadcrumbsScript.setAttribute("id", "seo-breadcrumbs-schema");
        document.head.appendChild(breadcrumbsScript);
      }
      breadcrumbsScript.textContent = JSON.stringify(breadcrumbData);
    } else if (breadcrumbsScript) {
      breadcrumbsScript.remove();
    }
  }, [title, description, keywords, canonical, path, ogImage, ogType, noIndex, breadcrumbs, location.pathname]);

  return null;
};

export default SEO;

